import React from 'react';
import { EnhancedPagination } from './EnhancedPagination';

export const ParticipantsTable = ({
  participants,
  totalParticipants,
  currentPage,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  sortField,
  sortDirection,
  onSort,
  onParticipantClick,
  filterStatus,
  setFilterStatus,
  formatCurrency,
  formatDate,
  searchParticipant,
  setSearchParticipant
}) => {
  const getStatusBadge = (status) => {
    const isPaid = status === 'Paid Amount';
    return (
      <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold transition-all duration-300 ${isPaid
        ? 'bg-gradient-to-r from-green-100 to-green-50 text-green-800 border border-green-200 shadow-sm'
        : 'bg-gradient-to-r from-red-100 to-red-50 text-red-800 border border-red-200 shadow-sm'
        }`}>
        {isPaid ? (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        ) : (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        )}
        {status}
      </span>
    );
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return (
      <span className="ml-1 text-blue-500">
        {sortDirection === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  return (
    <div className="bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-2xl border border-gray-200 overflow-hidden">
      {/* Header with Filters */}
      <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-white to-gray-50">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-blue-100 to-purple-100 flex items-center justify-center">
              <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13.5 0h-6m6 0V9a3 3 0 00-3-3h-1.5m6 0c0-1.657-1.343-3-3-3m-9 0c0 1.657 1.343 3 3 3m0 0h1.5M9 3C7.343 3 6 4.343 6 6m0 0v1.5M6 9c0 1.657 1.343 3 3 3m0 0h1.5" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-800">Participants</h3>
              <p className="text-sm text-gray-600">{totalParticipants} participant(s) found</p>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            {/* Search */}
            <div className="relative">
              <input
                type="text"
                placeholder="Search..."
                value={searchParticipant}
                onChange={(e) => {
                  setSearchParticipant(e.target.value);
                  onPageChange(1);
                }}
                className="w-64 px-4 py-2 pl-10 pr-8 border border-gray-300 rounded-xl focus:border-blue-500 focus:ring-0 bg-white shadow-sm"
              />
              <svg
                className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              {searchParticipant && (
                <button
                  onClick={() => setSearchParticipant('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-red-500"
                >
                  ×
                </button>
              )}
            </div>

            {/* Status Filter */}
            <div className="relative">
              <select
                className="px-4 py-2 pl-10 pr-8 border border-gray-300 rounded-xl focus:border-purple-500 focus:ring-0 bg-white shadow-sm appearance-none cursor-pointer"
                value={filterStatus}
                onChange={(e) => {
                  setFilterStatus(e.target.value);
                  onPageChange(1);
                }}
              >
                <option value="all">All Status</option>
                <option value="Paid Amount">Paid</option>
                <option value="Not Paid">Not Paid</option>
              </select>
              <svg
                className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
              {[
                { key: 'name', label: 'Participant' },
                { key: 'joinDate', label: 'Join Date' },
                { key: 'amount', label: 'Amount' },
                { key: 'status', label: 'Status' }
              ].map((column) => (
                <th
                  key={column.key}
                  className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200/50 transition-colors"
                  onClick={() => onSort(column.key)}
                >
                  <div className="flex items-center gap-2">
                    {column.label}
                    <SortIcon field={column.key} />
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {participants.map((participant, index) => (
              <tr
                key={participant.id}
                onClick={() => onParticipantClick(participant)}
                className="hover:bg-gradient-to-r hover:from-blue-50 hover:to-white transition-all duration-200 cursor-pointer group"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center text-sm font-bold text-blue-700 group-hover:scale-110 transition-transform">
                      {participant.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
                        {participant.name}
                      </div>
                      <div className="text-sm text-gray-500">{participant.email}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="inline-flex items-center gap-2 text-sm px-3 py-1.5 bg-gray-100 rounded-lg">
                    <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span className="font-medium text-gray-900">
                      {formatDate(participant.joinDate)}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className="font-bold text-gray-900">
                    {formatCurrency(participant.amount)}
                  </span>
                </td>
                <td className="px-6 py-4">
                  {getStatusBadge(participant.status)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {participants.length === 0 && (
          <div className="text-center py-16">
            <div className="inline-block p-8 bg-white rounded-2xl border border-gray-200">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-gray-600 mb-2">No participants found</h3>
              <p className="text-gray-500 mb-4">Try adjusting your filters</p>
              <button
                onClick={() => {
                  setFilterStatus('all');
                  setSearchParticipant('');
                }}
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                Reset Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Pagination */}
      <EnhancedPagination
        totalItems={totalParticipants}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage}
        onPageChange={onPageChange}
        onItemsPerPageChange={onItemsPerPageChange}
      />
    </div>
  );
};